import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {SelectionSystemType} from '../../model/selection-system-type';
import {UnitSelectionService} from '../../services/unit-selection-service';
import {StringUtils} from '../../utils/string-utils';
import {FluidType} from '../../model/fluid-type';
import {SureFlowLoopInitial} from '../../model/sure-flow-loop-initial';
import {RatingSureFlowService} from '../../services/rating-sure-flow.service';
import {SureFlowPipe} from '../../model/sure-flow-pipe';
import {MatButton} from '@angular/material/button';
import {MatOption} from '@angular/material/core';
import {MatSelect} from '@angular/material/select';
import {FormsModule} from '@angular/forms';
import {MatInput} from '@angular/material/input';
import {MatFormField, MatLabel} from '@angular/material/form-field';


@Component({
    selector: 'app-add-sure-flow-loop-dialog',
    templateUrl: './add-sure-flow-loop-dialog.component.html',
    styleUrls: ['./add-sure-flow-loop-dialog.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatLabel, MatInput, FormsModule, MatSelect, MatOption, MatButton]
})
export class AddSureFlowLoopDialogComponent implements OnInit {
  private readonly dialogRef = inject<MatDialogRef<AddSureFlowLoopDialogComponent>>(MatDialogRef);
  private readonly dialog = inject(MatDialog);
  private readonly selectionService = inject(UnitSelectionService);
  private readonly ratingSureFlowService = inject(RatingSureFlowService);
  private readonly data = inject(MAT_DIALOG_DATA);


  systemTypes: SelectionSystemType[];
  readonly fluidTypes = signal<FluidType[]>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  selectedSystemTypeCode!: string;
  showCoolingFluid!: boolean;
  coolingFluidTypeCode!: string;
  coolingGlycolPercent!: number;
  showHeatingFluid!: boolean;
  heatingFluidTypeCode!: string;
  heatingGlycolPercent!: number;
  description!: string;
  metricInput!: boolean;
  private readonly defaultPipe = signal<SureFlowPipe>(undefined!);

  constructor() {
    const data = this.data;

    this.systemTypes = data.systemTypes;
  }

  ngOnInit(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.metricInput = false;
    this.ratingSureFlowService.getSureFlowLoopPipes().subscribe({
      next: (pipes) => {
        this.defaultPipe.set(pipes.sort((first, second) => first.InsideDiameter - second.InsideDiameter)[0]);
        this.selectionService.getFluidTypes().subscribe({
          next: (fluidTypes) => {
            this.fluidTypes.set(fluidTypes);
            this.isBusy.set(false);
          }, error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.formatErrorMessage(error));
  }

  isGlycol(fluidTypeCode: string): boolean {
    const fluidType = this.getFluidType(fluidTypeCode);
    return fluidType?.glycol;
  }

  private getFluidType(code: string): FluidType {
    if (StringUtils.isNotBlank(code) && (this.fluidTypes()?.length || 0) > 0) {
      const filtered: FluidType[] = this.fluidTypes().filter(type => StringUtils.equalsIgnoreCase(type.code, code));
      if (filtered.length === 1) {
        return filtered[0];
      }
    }
    return undefined!;
  }

  private getSelectedSystemType(): SelectionSystemType {
    if (StringUtils.isNotBlank(this.selectedSystemTypeCode) && (this.systemTypes?.length || 0) > 0) {
      const filtered: SelectionSystemType[] = this.systemTypes.filter(type => StringUtils.equalsIgnoreCase(type.code, this.selectedSystemTypeCode));
      if (filtered.length === 1) {
        return filtered[0];
      }
    }
    return undefined!;
  }

  systemTypeChanged(): void {
    this.showCoolingFluid = false;
    this.coolingGlycolPercent = undefined!;
    this.showHeatingFluid = false;
    this.heatingGlycolPercent = undefined!;
    const systemType = this.getSelectedSystemType();
    this.showCoolingFluid = StringUtils.equalsIgnoreCase('C', systemType?.primaryCoilApplication);
    this.showHeatingFluid = StringUtils.equalsIgnoreCase('H', systemType?.primaryCoilApplication) || StringUtils.equalsIgnoreCase('H', systemType?.secondaryCoilApplication);
    if (this.showCoolingFluid) {
      this.coolingFluidTypeCode = 'W';
    } else {
      this.coolingFluidTypeCode = undefined!;
    }
    if (this.showHeatingFluid) {
      this.heatingFluidTypeCode = 'W';
    } else {
      this.heatingFluidTypeCode = undefined!;
    }
  }

  canSave(): boolean {
    if (StringUtils.isBlank(this.selectedSystemTypeCode)) {
      return false;
    }
    if (this.showCoolingFluid && StringUtils.isBlank(this.coolingFluidTypeCode)) {
      return false;
    }
    if (this.isGlycol(this.coolingFluidTypeCode)) {
      if (isNaN(this.coolingGlycolPercent)) {
        return false;
      }
    }
    if (this.showHeatingFluid && StringUtils.isBlank(this.heatingFluidTypeCode)) {
      return false;
    }
    if (this.isGlycol(this.heatingFluidTypeCode)) {
      if (isNaN(this.heatingGlycolPercent)) {
        return false;
      }
    }
    return true;
  }

  save(): void {
    if (this.canSave()) {
      const result = new SureFlowLoopInitial();
      result.metric = this.metricInput;
      result.systemTypeCode = this.selectedSystemTypeCode;
      result.description = this.description || this.getSelectedSystemType().description;
      if (this.showCoolingFluid) {
        result.coolingPipeId = this.defaultPipe().Id;
        result.coolingFluidFlowRate = this.defaultPipe().MaxFlowRate;
        result.coolingFluidTypeCode = this.coolingFluidTypeCode;
        result.coolingGlycolPercentage = this.coolingGlycolPercent;
      }
      if (this.showHeatingFluid) {
        result.heatingPipeId = this.defaultPipe().Id;
        result.heatingFluidFlowRate = this.defaultPipe().MaxFlowRate;
        result.heatingFluidTypeCode = this.heatingFluidTypeCode;
        result.heatingGlycolPercentage = this.heatingGlycolPercent;
      }
      this.dialogRef.close(result);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

}
